// Analytics — single entry point for PostHog + a lightweight wrapper API.
// Vercel Web Analytics + Speed Insights are mounted separately as React
// components in src/index.js since they auto-track page views.
//
// Setup notes:
//   1. Get a project API key from https://app.posthog.com or the EU
//      cloud at https://eu.posthog.com. Key looks like "phc_…".
//   2. Add to .env / Vercel project env vars:
//        REACT_APP_POSTHOG_KEY=phc_…
//        REACT_APP_POSTHOG_HOST=https://eu.posthog.com   (or US default)
//   3. PostHog dashboard → Settings → enable Session Replay if you
//      want replays. Privacy mask is on by default.
//
// If REACT_APP_POSTHOG_KEY is unset (e.g. local dev), every call below
// is a no-op so the app still works.

import posthog from 'posthog-js';

const KEY  = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://app.posthog.com';

let initialized = false;

export function initAnalytics() {
  if (initialized || !KEY) return;
  if (typeof window === 'undefined') return;

  posthog.init(KEY, {
    api_host: HOST,
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: true,
    // Session replay is enabled in the PostHog dashboard. These ensure
    // sensitive form fields (password, payment, KYC) are masked even if
    // someone forgets to add the data-attr at the component level.
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
        creditCard: true,
        // PII fields by name pattern.
      },
      maskInputFn: (text, element) => {
        const sensitive = ['password', 'card', 'cvv', 'iban', 'passport', 'emirates_id'];
        const name = (element?.getAttribute?.('name') || '').toLowerCase();
        const id   = (element?.id || '').toLowerCase();
        if (sensitive.some((s) => name.includes(s) || id.includes(s))) return '****';
        return text;
      },
    },
    loaded: (ph) => {
      if (process.env.NODE_ENV === 'development') ph.debug?.(false);
    },
  });
  initialized = true;

  // Attribution super-properties. UTM params overwrite on every visit so a
  // returning visitor coming back via a different campaign gets the new
  // attribution. Referrer + landing path use register_once so the *first*
  // touch survives — useful for measuring multi-visit conversion.
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']) {
      const v = params.get(k);
      if (v) utm[k] = v;
    }
    if (Object.keys(utm).length) posthog.register(utm);
    posthog.register_once({
      landing_first_seen: window.location.pathname,
      referrer: document.referrer || '(direct)',
    });
  } catch { /* silent */ }
}

export function identifyUser(user, profile) {
  if (!initialized || !user) return;
  posthog.identify(user.id, {
    email: user.email,
    role: profile?.role || 'investor',
    broker_verified: !!profile?.broker_verified,
    subscription_status: profile?.subscription_status || 'free',
    onboarded: !!profile?.onboarded,
    created_at: user.created_at,
  });
}

export function resetIdentity() {
  if (!initialized) return;
  try { posthog.reset(); } catch { /* silent */ }
}

// Track a named event with optional properties. Use sparingly — autocapture
// already covers most clicks/page-views; reserve track() for the moments
// you'd want to build a funnel from.
export function track(event, properties) {
  if (!initialized) return;
  try { posthog.capture(event, properties || {}); } catch { /* silent */ }
}

// Convenience event names — one place to keep them consistent across the app.
export const Events = {
  SIGNUP_COMPLETED:      'signup_completed',
  TOUR_STEP_VIEWED:      'tour_step_viewed',
  TOUR_COMPLETED:        'tour_completed',
  ASK_AI_OPENED:         'ask_ai_opened',
  ASK_AI_QUERY:          'ask_ai_query',
  ASK_AI_TOOL_CALLED:    'ask_ai_tool_called',
  SAVED_SEARCH_CREATED:  'saved_search_created',
  DEAL_ANALYZED:         'deal_analyzed',
  SIGNAL_OPENED:         'signal_opened',
  BUILDING_OPENED:       'building_opened',
  WATCHLIST_ADDED:       'watchlist_added',
  AGENCY_CREATED:        'agency_created',
  AGENCY_INVITE_SENT:    'agency_invite_sent',
  UPGRADE_CLICKED:       'upgrade_clicked',
  CHECKOUT_STARTED:      'checkout_started',
  // Server-side (fired from api/_lib/server-analytics.js via posthog-node).
  // Listed here so the client has the same source-of-truth for event names.
  CHECKOUT_SESSION_CREATED: 'checkout_session_created',
  SUBSCRIPTION_ACTIVATED: 'subscription_activated',
  SUBSCRIPTION_CANCELED:  'subscription_canceled',
  LANDING_CTA_CLICKED:   'landing_cta_clicked',
  LANDING_PLAN_CTA_CLICKED: 'landing_plan_cta_clicked',
  LANDING_SECTION_VIEWED: 'landing_section_viewed',
  LANDING_NAV_CLICKED:   'landing_nav_clicked',
  LANDING_FAQ_OPENED:    'landing_faq_opened',
  PRICING_TOGGLE:        'pricing_toggle',
  LOGIN_TAB_SWITCHED:    'login_tab_switched',
  DEMO_LOGIN_STARTED:    'demo_login_started',
  DEMO_QUOTA_HIT:        'demo_quota_hit',
};
